<template>
    <v-card data-testid="ai-input-schema__card" outlined class="mb-4">
        <v-card-title data-testid="ai-input-schema__title" class="pa-3 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
                <v-icon left small>mdi-database-cog-outline</v-icon>
                <h5 class="font-weight-medium">Input Configuration</h5>
            </div>
            <div v-if="editable" class="d-flex align-center">
                <span class="mr-2 text-body-2">Config Mode</span>
                <v-switch
                    data-testid="ai-input-schema__toggle"
                    v-model="useInputSchema"
                    inset color="success"
                    :false-value="false" :true-value="true"
                    hide-details class="mt-0 pt-0"
                    @change="onToggleChange"
                />
            </div>
            <v-chip v-else-if="useInputSchema" small color="success" outlined>
                <v-icon left small>mdi-check-circle</v-icon>Configured
            </v-chip>
            <v-chip v-else small color="grey" outlined>Legacy</v-chip>
        </v-card-title>

        <v-card-text v-if="useInputSchema" class="pa-3">
            <!-- Loading -->
            <div v-if="schemaLoading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" size="32" />
                <div class="mt-2 grey--text text-body-2">Loading schema...</div>
            </div>

            <!-- Schema editor / viewer -->
            <template v-else-if="localSchema">
                <v-tabs v-model="activeTab" class="mb-3" height="36">
                    <v-tab data-testid="ai-input-schema__tab-fields">Field Mappings</v-tab>
                    <v-tab data-testid="ai-input-schema__tab-composition">Composition</v-tab>
                    <v-tab data-testid="ai-input-schema__tab-prompt-editor">Prompt Editor</v-tab>
                    <v-tab data-testid="ai-input-schema__tab-preview">Preview</v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab">
                    <!-- Tab 0: Field Mappings -->
                    <v-tab-item>
                        <!-- Field Mappings — Card-based layout, cardinality-first -->
                        <div data-testid="ai-input-schema__field-cards">
                            <v-card v-for="(field, idx) in localSchema.field_mappings" :key="'fm-card-' + idx"
                                outlined class="mb-3 field-mapping-card">
                                <!-- Card Header: Alias + Cardinality + Actions -->
                                <v-card-title class="pa-2 d-flex align-center" style="min-height:40px">
                                    <v-row dense align="center" no-gutters>
                                        <v-col cols="12" sm="3">
                                            <v-text-field v-if="editable" v-model="field.alias"
                                                dense hide-details outlined class="schema-input"
                                                label="Alias (prompt placeholder)" placeholder="${MyAlias}" />
                                            <span v-else class="font-weight-medium">${{ field.alias }}</span>
                                        </v-col>
                                        <v-col cols="12" sm="2" class="pl-2">
                                            <v-select v-if="editable" v-model="field.cardinality"
                                                :items="cardinalityOptions" dense hide-details outlined class="schema-input"
                                                label="Cardinality" @change="onCardinalityChange(idx)" />
                                            <v-chip v-else small outlined :color="field.cardinality === 'scalar' ? 'grey' : 'primary'">
                                                {{ field.cardinality || 'scalar' }}
                                            </v-chip>
                                        </v-col>
                                        <v-col cols="12" sm="2" class="pl-2">
                                            <v-text-field v-if="editable"
                                                :value="formatApplicableWhen(field.applicable_when)"
                                                @change="parseApplicableWhen(field, $event)"
                                                dense hide-details outlined class="schema-input"
                                                label="Applicable When" placeholder="type_of_data=proforma" />
                                            <span v-else class="text-caption grey--text">{{ formatApplicableWhen(field.applicable_when) || '—' }}</span>
                                        </v-col>
                                        <v-spacer />
                                        <v-col cols="auto">
                                            <v-btn v-if="editable" icon x-small color="error" @click="removeFieldMapping(idx)">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-title>

                                <v-divider />

                                <!-- SCALAR body -->
                                <v-card-text v-if="!field.cardinality || field.cardinality === 'scalar'" class="pa-3">
                                    <v-row dense>
                                        <v-col cols="12" sm="3">
                                            <v-autocomplete v-if="editable" v-model="field.source_collection"
                                                :items="availableCollections" :loading="collectionsLoading"
                                                dense hide-details outlined class="schema-input"
                                                label="Source Collection"
                                                @change="onCollectionChange(field, idx)" />
                                            <div v-else class="text-caption">{{ field.source_collection }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-autocomplete v-if="editable" v-model="field.source_field"
                                                :items="getFieldsForRow(idx)"
                                                :loading="fieldLoadingIndex === idx"
                                                dense hide-details outlined class="schema-input"
                                                label="Source Field"
                                                @change="onSourceFieldChange(field, idx)" />
                                            <div v-else class="text-caption">{{ field.source_field }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <v-select v-if="editable" v-model="field.data_type"
                                                :items="dataTypes" dense hide-details outlined class="schema-input"
                                                label="Data Type" />
                                            <div v-else class="text-caption">{{ field.data_type }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <v-text-field v-if="editable" v-model="field.default_value"
                                                dense hide-details outlined class="schema-input"
                                                label="Default" placeholder="(none)" />
                                            <div v-else class="text-caption">{{ field.default_value || '—' }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="1">
                                            <v-checkbox v-if="editable" v-model="field.required"
                                                hide-details class="mt-0 pt-0" label="Req" />
                                            <v-icon v-else small :color="field.required ? 'success' : 'grey'">
                                                {{ field.required ? 'mdi-check' : 'mdi-minus' }}
                                            </v-icon>
                                        </v-col>
                                    </v-row>
                                    <!-- ObjectId Lookup config — shown when field is an ObjectId reference -->
                                    <v-row v-if="editable && getRefInfo(field)" dense class="mt-2">
                                        <v-col cols="12">
                                            <v-alert dense type="info" text class="mb-2 py-1">
                                                <span class="text-caption">
                                                    <v-icon small left>mdi-link-variant</v-icon>
                                                    This field is an ObjectId referencing
                                                    <strong>{{ getRefInfo(field).ref_collection }}</strong>
                                                </span>
                                            </v-alert>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete v-model="field.lookup_collection"
                                                :items="[getRefInfo(field).ref_collection]"
                                                dense hide-details outlined class="schema-input"
                                                label="Lookup Collection" readonly />
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-combobox v-model="field.lookup_field"
                                                :items="getLookupFields(field)"
                                                dense hide-details outlined class="schema-input"
                                                label="Lookup Field (value to inject)"
                                                placeholder="e.g. name"
                                                :menu-props="{ maxHeight: 200 }" />
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-btn v-if="!field.lookup_collection" x-small text color="primary"
                                                @click="applyLookup(field, idx)">
                                                <v-icon left x-small>mdi-link-plus</v-icon>Configure Lookup
                                            </v-btn>
                                            <v-btn v-else x-small text color="error"
                                                @click="clearLookup(field)">
                                                <v-icon left x-small>mdi-link-off</v-icon>Remove Lookup
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>

                                <!-- OBJECT body -->
                                <v-card-text v-else-if="field.cardinality === 'object'" class="pa-3">
                                    <v-row dense class="mb-2">
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete v-if="editable" v-model="field.source_collection"
                                                :items="availableCollections" :loading="collectionsLoading"
                                                dense hide-details outlined class="schema-input"
                                                label="Source Collection"
                                                @change="onCollectionChange(field, idx)" />
                                            <div v-else class="text-caption">Collection: {{ field.source_collection }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field v-if="editable" v-model="field.format.template"
                                                dense hide-details outlined class="schema-input"
                                                label="Format (optional)"
                                                :placeholder="getObjectTemplatePlaceholder(field)"
                                                hint="Leave empty for JSON. Use ${key} syntax for custom format."
                                                persistent-hint />
                                            <div v-else class="text-caption text--secondary">
                                                {{ field.format.template || '(JSON output)' }}
                                            </div>
                                        </v-col>
                                    </v-row>
                                    <!-- Object keys table -->
                                    <h6 class="font-weight-medium mb-1 text-caption">
                                        Object Keys
                                        <span class="grey--text ml-1">(each key maps to a field from the collection)</span>
                                    </h6>
                                    <v-simple-table dense class="schema-table nested-table">
                                        <thead>
                                            <tr>
                                                <th>Key Name (Alias)</th>
                                                <th>Source Field</th>
                                                <th>Type</th>
                                                <th>Default Value</th>
                                                <th v-if="editable" width="50"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="(nm, nIdx) in field.nested_mappings">
                                                <tr :key="'obj-nm-' + idx + '-' + nIdx">
                                                    <td>
                                                        <v-text-field v-if="editable" v-model="nm.alias"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="e.g. amountproforma" />
                                                        <span v-else>{{ nm.alias }}</span>
                                                    </td>
                                                    <td>
                                                        <v-combobox v-if="editable" v-model="nm.source_field"
                                                            :items="getFieldsForRow(idx)"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="e.g. MultiCriteriaComparison.AmountComparator1"
                                                            :menu-props="{ maxHeight: 200 }"
                                                            @change="onNestedSourceFieldChange(field, nm)" />
                                                        <span v-else>{{ nm.source_field }}</span>
                                                    </td>
                                                    <td>
                                                        <v-select v-if="editable" v-model="nm.data_type"
                                                            :items="dataTypes" dense hide-details outlined class="schema-input" />
                                                        <span v-else>{{ nm.data_type }}</span>
                                                    </td>
                                                    <td>
                                                        <v-text-field v-if="editable" v-model="nm.default_value"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="(none)" />
                                                        <span v-else>{{ nm.default_value || '—' }}</span>
                                                    </td>
                                                    <td v-if="editable">
                                                        <v-btn icon x-small color="error" @click="removeNestedMapping(idx, nIdx)">
                                                            <v-icon x-small>mdi-delete</v-icon>
                                                        </v-btn>
                                                    </td>
                                                </tr>
                                                <!-- Lookup config row for ObjectId nested fields -->
                                                <tr v-if="editable && getNestedRefInfo(field, nm)" :key="'obj-nm-lookup-' + idx + '-' + nIdx" class="lookup-row">
                                                    <td :colspan="5" class="pa-1 pl-4">
                                                        <div class="d-flex align-center" style="gap:8px">
                                                            <v-icon x-small color="info">mdi-link-variant</v-icon>
                                                            <span class="text-caption grey--text text--darken-1">
                                                                ObjectId → <strong>{{ getNestedRefInfo(field, nm).ref_collection }}</strong>
                                                            </span>
                                                            <v-combobox v-model="nm.lookup_field"
                                                                :items="getNestedLookupFields(nm)"
                                                                dense hide-details outlined class="schema-input"
                                                                style="max-width:200px"
                                                                placeholder="field to resolve"
                                                                :menu-props="{ maxHeight: 200 }" />
                                                            <v-btn v-if="nm.lookup_collection" x-small icon color="error"
                                                                @click="clearLookup(nm)">
                                                                <v-icon x-small>mdi-link-off</v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </template>
                                            <tr v-if="!field.nested_mappings || !field.nested_mappings.length">
                                                <td :colspan="editable ? 5 : 4" class="text-center grey--text py-2">
                                                    No keys defined — add keys to build the object
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                    <v-btn v-if="editable" text x-small color="primary" class="mt-1"
                                        @click="addNestedMapping(idx)">
                                        <v-icon left x-small>mdi-plus</v-icon>Add Key
                                    </v-btn>
                                    <!-- Live preview of resolved object -->
                                    <div v-if="field.nested_mappings && field.nested_mappings.length" class="mt-2 pa-2 rounded" style="background:#f5f5f5;font-family:monospace;font-size:12px">
                                        <span class="grey--text text--darken-1">Prompt injects →</span>
                                        <span class="ml-1">{{ getObjectPreview(field) }}</span>
                                    </div>
                                </v-card-text>

                                <!-- ARRAY body -->
                                <v-card-text v-else-if="field.cardinality === 'array'" class="pa-3">
                                    <v-row dense class="mb-2">
                                        <v-col cols="12" sm="3">
                                            <v-autocomplete v-if="editable" v-model="field.source_collection"
                                                :items="availableCollections" :loading="collectionsLoading"
                                                dense hide-details outlined class="schema-input"
                                                label="Source Collection"
                                                @change="onCollectionChange(field, idx)" />
                                            <div v-else class="text-caption">Collection: {{ field.source_collection }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-combobox v-if="editable" v-model="field.source_field"
                                                :items="getFieldsForRow(idx)"
                                                dense hide-details outlined class="schema-input"
                                                label="Array Path"
                                                placeholder="e.g. TopMovers"
                                                :menu-props="{ maxHeight: 200 }" />
                                            <div v-else class="text-caption">Path: {{ field.source_field }}</div>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <v-text-field v-if="editable" v-model="field.format.item_template"
                                                dense hide-details outlined class="schema-input"
                                                label="Item Template"
                                                :placeholder="getArrayItemTemplatePlaceholder(field)" />
                                            <div v-else class="text-caption">Template: {{ field.format.item_template }}</div>
                                        </v-col>
                                        <v-col cols="6" sm="1">
                                            <v-text-field v-if="editable" v-model="field.format.separator"
                                                dense hide-details outlined class="schema-input"
                                                label="Sep" placeholder="\n" />
                                        </v-col>
                                        <v-col cols="6" sm="2">
                                            <v-text-field v-if="editable" v-model="field.format.empty_text"
                                                dense hide-details outlined class="schema-input"
                                                label="Empty Text" placeholder="No data" />
                                        </v-col>
                                    </v-row>
                                    <!-- Array item fields table -->
                                    <h6 class="font-weight-medium mb-1 text-caption">
                                        Item Fields
                                        <span class="grey--text ml-1">(fields resolved per array item)</span>
                                    </h6>
                                    <v-simple-table dense class="schema-table nested-table">
                                        <thead>
                                            <tr>
                                                <th>Field Name (Alias)</th>
                                                <th>Source Field (in item)</th>
                                                <th>Type</th>
                                                <th>Default Value</th>
                                                <th v-if="editable" width="50"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="(nm, nIdx) in field.nested_mappings">
                                                <tr :key="'arr-nm-' + idx + '-' + nIdx">
                                                    <td>
                                                        <v-text-field v-if="editable" v-model="nm.alias"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="e.g. Name" />
                                                        <span v-else>{{ nm.alias }}</span>
                                                    </td>
                                                    <td>
                                                        <v-combobox v-if="editable" v-model="nm.source_field"
                                                            :items="getNestedFieldsForRow(idx)"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="e.g. name"
                                                            :menu-props="{ maxHeight: 200 }"
                                                            @change="onNestedSourceFieldChange(field, nm)" />
                                                        <span v-else>{{ nm.source_field }}</span>
                                                    </td>
                                                    <td>
                                                        <v-select v-if="editable" v-model="nm.data_type"
                                                            :items="dataTypes" dense hide-details outlined class="schema-input" />
                                                        <span v-else>{{ nm.data_type }}</span>
                                                    </td>
                                                    <td>
                                                        <v-text-field v-if="editable" v-model="nm.default_value"
                                                            dense hide-details outlined class="schema-input"
                                                            placeholder="(none)" />
                                                        <span v-else>{{ nm.default_value || '—' }}</span>
                                                    </td>
                                                    <td v-if="editable">
                                                        <v-btn icon x-small color="error" @click="removeNestedMapping(idx, nIdx)">
                                                            <v-icon x-small>mdi-delete</v-icon>
                                                        </v-btn>
                                                    </td>
                                                </tr>
                                                <!-- Lookup config row for ObjectId nested fields -->
                                                <tr v-if="editable && getNestedRefInfo(field, nm)" :key="'arr-nm-lookup-' + idx + '-' + nIdx" class="lookup-row">
                                                    <td :colspan="5" class="pa-1 pl-4">
                                                        <div class="d-flex align-center" style="gap:8px">
                                                            <v-icon x-small color="info">mdi-link-variant</v-icon>
                                                            <span class="text-caption grey--text text--darken-1">
                                                                ObjectId → <strong>{{ getNestedRefInfo(field, nm).ref_collection }}</strong>
                                                            </span>
                                                            <v-combobox v-model="nm.lookup_field"
                                                                :items="getNestedLookupFields(nm)"
                                                                dense hide-details outlined class="schema-input"
                                                                style="max-width:200px"
                                                                placeholder="field to resolve"
                                                                :menu-props="{ maxHeight: 200 }" />
                                                            <v-btn v-if="nm.lookup_collection" x-small icon color="error"
                                                                @click="clearLookup(nm)">
                                                                <v-icon x-small>mdi-link-off</v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </template>
                                            <tr v-if="!field.nested_mappings || !field.nested_mappings.length">
                                                <td :colspan="editable ? 5 : 4" class="text-center grey--text py-2">
                                                    No item fields defined
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                    <v-btn v-if="editable" text x-small color="primary" class="mt-1"
                                        @click="addNestedMapping(idx)">
                                        <v-icon left x-small>mdi-plus</v-icon>Add Item Field
                                    </v-btn>
                                    <!-- Live preview -->
                                    <div v-if="field.nested_mappings && field.nested_mappings.length" class="mt-2 pa-2 rounded" style="background:#f5f5f5;font-family:monospace;font-size:12px">
                                        <span class="grey--text text--darken-1">Prompt injects →</span>
                                        <span class="ml-1">{{ getArrayPreview(field) }}</span>
                                    </div>
                                </v-card-text>
                            </v-card>

                            <div v-if="!localSchema.field_mappings.length" class="text-center grey--text py-3">
                                No field mappings defined
                            </div>
                        </div>
                        <v-btn v-if="editable" text small color="primary" class="mt-2" @click="addFieldMapping">
                            <v-icon left small>mdi-plus</v-icon>Add Field
                        </v-btn>
                        <v-btn v-if="editable" small color="success" class="mt-2 ml-2" depressed
                            :loading="schemaSaving" @click="saveSchema">
                            <v-icon left small>mdi-content-save</v-icon>Save Schema
                        </v-btn>
                    </v-tab-item>

                    <!-- Tab 1: Composition Rules -->
                    <v-tab-item>
                        <v-row class="pa-3">
                            <v-col cols="12" sm="6">
                                <h6 class="font-weight-medium mb-2">System Message</h6>
                                <v-chip-group v-if="!editable" column>
                                    <v-chip v-for="pt in localSchema.composition.system_message" :key="'sys-' + pt"
                                        small class="ma-1" color="primary" outlined>{{ pt }}</v-chip>
                                </v-chip-group>
                                <v-select v-else v-model="localSchema.composition.system_message"
                                    :items="promptTypes" multiple chips small-chips deletable-chips
                                    dense outlined hide-details label="Prompt types in system message" />
                            </v-col>
                            <v-col cols="12" sm="6">
                                <h6 class="font-weight-medium mb-2">User Message</h6>
                                <v-chip-group v-if="!editable" column>
                                    <v-chip v-for="pt in localSchema.composition.user_message" :key="'usr-' + pt"
                                        small class="ma-1" color="accent" outlined>{{ pt }}</v-chip>
                                </v-chip-group>
                                <v-select v-else v-model="localSchema.composition.user_message"
                                    :items="promptTypes" multiple chips small-chips deletable-chips
                                    dense outlined hide-details label="Prompt types in user message" />
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- Tab 2: Prompt Editor -->
                    <v-tab-item>
                        <div class="pa-3">
                            <p class="caption grey--text mb-3">
                                Edit prompt templates below. Type <code>$$</code> to autocomplete available schema aliases.
                            </p>
                            <div v-if="promptEditorLoading" class="text-center py-4">
                                <v-progress-circular indeterminate color="primary" size="24" />
                                <span class="ml-2 grey--text text-body-2">Loading prompts...</span>
                            </div>
                            <template v-else>
                                <v-card v-for="pt in editablePromptTypes" :key="'pe-' + pt.type"
                                    outlined class="mb-3">
                                    <v-card-title class="pa-3 d-flex align-center justify-space-between">
                                        <div class="d-flex align-center">
                                            <v-icon small left>mdi-text-box-outline</v-icon>
                                            <h6 class="font-weight-medium text-capitalize">{{ pt.type }}</h6>
                                        </div>
                                        <div class="d-flex align-center">
                                            <v-chip x-small class="mr-2" color="info" outlined v-if="pt.name">
                                                {{ pt.name }}
                                            </v-chip>
                                            <v-chip x-small class="mr-2" color="grey" outlined v-if="pt.type === 'guard_rails'">
                                                read-only
                                            </v-chip>
                                            <v-btn v-if="editable && pt.type !== 'guard_rails'" icon x-small color="primary"
                                                :loading="pt._saving"
                                                @click="savePrompt(pt)"
                                                title="Save this prompt">
                                                <v-icon small>mdi-content-save</v-icon>
                                            </v-btn>
                                        </div>
                                    </v-card-title>
                                    <v-card-text class="pa-3 pt-0">
                                        <div class="prompt-editor-wrapper" style="position: relative;">
                                            <v-textarea
                                                v-model="pt.content"
                                                :readonly="!editable || pt.type === 'guard_rails'"
                                                outlined dense hide-details
                                                rows="8" auto-grow
                                                class="prompt-editor-textarea"
                                                placeholder="Type your prompt here... use $$ to insert field aliases"
                                                @input="onPromptInput($event, pt)"
                                                @keydown="onPromptKeydown($event, pt)"
                                                :ref="'promptTextarea_' + pt.type"
                                            />
                                            <!-- Autocomplete dropdown -->
                                            <v-card v-if="autocomplete.visible && autocomplete.promptType === pt.type"
                                                class="prompt-autocomplete-menu"
                                                elevation="4"
                                                :style="{ top: autocomplete.top + 'px', left: autocomplete.left + 'px' }">
                                                <v-list dense class="py-0" style="max-height: 200px; overflow-y: auto;">
                                                    <v-list-item
                                                        v-for="(item, aIdx) in filteredAutocompleteItems"
                                                        :key="'ac-' + aIdx"
                                                        :class="{ 'v-list-item--active': aIdx === autocomplete.selectedIndex }"
                                                        @click="insertAutocomplete(item, pt)"
                                                        dense>
                                                        <v-list-item-icon class="mr-2">
                                                            <v-icon x-small :color="sourceColor(item.source)">mdi-circle</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-content>
                                                            <v-list-item-title class="text-body-2">
                                                                <code class="px-1">${{ '{' + item.alias + '}' }}</code>
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle class="text-caption">
                                                                {{ item.source }} &middot; {{ item.dataType }}
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                    <v-list-item v-if="!filteredAutocompleteItems.length" dense>
                                                        <v-list-item-content>
                                                            <v-list-item-title class="grey--text text-body-2">No matching aliases</v-list-item-title>
                                                        </v-list-item-content>
                                                    </v-list-item>
                                                </v-list>
                                            </v-card>
                                        </div>
                                    </v-card-text>
                                </v-card>
                                <div v-if="!editablePromptTypes.length" class="text-center grey--text py-4">
                                    <v-icon class="mb-2">mdi-information-outline</v-icon>
                                    <div>No prompt templates available for this feature. Create prompts first.</div>
                                </div>
                            </template>
                        </div>
                    </v-tab-item>

                    <!-- Tab 7: Preview -->
                    <v-tab-item>
                        <div class="pa-3">
                            <!-- Preview Controls -->
                            <v-card outlined class="mb-3 pa-3">
                                <div class="d-flex align-center justify-space-between mb-2">
                                    <h6 class="font-weight-medium">Preview Settings</h6>
                                    <div>
                                        <v-btn text x-small color="secondary" @click="generateSampleValues" class="mr-2">
                                            <v-icon left x-small>mdi-refresh</v-icon>Regenerate Samples
                                        </v-btn>
                                        <v-btn color="primary" small depressed :loading="previewLoading"
                                            @click="runPreview"
                                            data-testid="ai-input-schema__preview-btn">
                                            <v-icon left small>mdi-eye</v-icon>Generate Preview
                                        </v-btn>
                                        <v-btn color="deep-purple" small depressed :loading="testPromptLoading"
                                            @click="runTestPrompt" class="ml-2"
                                            data-testid="ai-input-schema__test-btn">
                                            <v-icon left small>mdi-robot</v-icon>Test with AI
                                        </v-btn>
                                    </div>
                                </div>
                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <v-select v-model="previewCycleType"
                                            :items="['proforma', 'forecast']"
                                            label="Cycle Type" dense outlined hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-select v-model="previewTypeOfData"
                                            :items="['proforma', 'forecast']"
                                            label="Type of Data" dense outlined hide-details />
                                    </v-col>
                                </v-row>

                                <!-- Editable Sample Values -->
                                <v-divider class="my-3" />
                                <div class="d-flex align-center mb-2">
                                    <h6 class="font-weight-medium">Sample Values</h6>
                                    <v-chip x-small class="ml-2" color="info" outlined>
                                        {{ Object.keys(previewSampleValues).length }} fields
                                    </v-chip>
                                </div>
                                <v-simple-table dense class="schema-table" v-if="Object.keys(previewSampleValues).length">
                                    <thead>
                                        <tr>
                                            <th style="width: 25%">Alias / Key</th>
                                            <th style="width: 15%">Source</th>
                                            <th style="width: 15%">Type</th>
                                            <th>Sample Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="entry in sampleValueEntries" :key="entry.key">
                                            <td class="font-weight-medium text-no-wrap">
                                                <code class="px-1">${{ '{' + entry.key + '}' }}</code>
                                            </td>
                                            <td>
                                                <v-chip x-small :color="sourceColor(entry.source)" outlined>
                                                    {{ entry.source }}
                                                </v-chip>
                                            </td>
                                            <td class="grey--text text-caption">{{ entry.dataType }}</td>
                                            <td>
                                                <v-text-field v-model="previewSampleValues[entry.key]"
                                                    dense hide-details outlined class="schema-input" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                                <div v-else class="text-center grey--text py-3 text-body-2">
                                    No field mappings or inputs defined in the schema yet
                                </div>
                            </v-card>

                            <template v-if="previewData">
                                <!-- Warnings & Errors -->
                                <v-alert v-if="previewData.unreplaced_placeholders && previewData.unreplaced_placeholders.length"
                                    type="warning" dense outlined class="mb-3">
                                    <strong>Unreplaced placeholders:</strong>
                                    {{ previewData.unreplaced_placeholders.join(', ') }}
                                </v-alert>

                                <v-alert v-if="previewData.warnings && previewData.warnings.length"
                                    type="info" dense outlined class="mb-3">
                                    <div v-for="(w, wi) in previewData.warnings" :key="wi">{{ w }}</div>
                                </v-alert>

                                <!-- Schema Version & Token Estimate -->
                                <v-row class="mb-3">
                                    <v-col cols="auto">
                                        <v-chip small color="primary" outlined>
                                            <v-icon left small>mdi-tag</v-icon>
                                            Schema v{{ previewData.schema_version || '—' }}
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-chip small color="teal" outlined>
                                            <v-icon left small>mdi-counter</v-icon>
                                            ~{{ estimatedTokens }} tokens
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="auto" v-if="previewData.prompt_structure">
                                        <v-chip small color="deep-purple" outlined>
                                            <v-icon left small>mdi-puzzle</v-icon>
                                            System: {{ (previewData.prompt_structure.system || []).join(' + ') }}
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="auto" v-if="previewData.prompt_structure">
                                        <v-chip small color="orange" outlined>
                                            <v-icon left small>mdi-puzzle</v-icon>
                                            User: {{ (previewData.prompt_structure.user || []).join(' + ') }}
                                        </v-chip>
                                    </v-col>
                                </v-row>

                                <!-- Full Prompt View Toggle -->
                                <div class="d-flex align-center mb-2">
                                    <v-btn-toggle v-model="previewViewMode" mandatory dense>
                                        <v-btn small value="split">
                                            <v-icon small left>mdi-view-split-vertical</v-icon>Split
                                        </v-btn>
                                        <v-btn small value="full">
                                            <v-icon small left>mdi-text-long</v-icon>Full Prompt
                                        </v-btn>
                                        <v-btn small value="data">
                                            <v-icon small left>mdi-database</v-icon>Data Map
                                        </v-btn>
                                    </v-btn-toggle>
                                    <v-spacer />
                                    <v-btn icon small @click="copyPreviewToClipboard" title="Copy full prompt">
                                        <v-icon small>mdi-content-copy</v-icon>
                                    </v-btn>
                                </div>

                                <!-- Split View -->
                                <template v-if="previewViewMode === 'split'">
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <h6 class="font-weight-medium mb-1">
                                                <v-icon small class="mr-1">mdi-robot</v-icon>System Message
                                            </h6>
                                            <v-card outlined class="preview-card">
                                                <v-card-text class="pa-3">
                                                    <pre class="preview-content">{{ previewData.system_message || '(empty)' }}</pre>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <h6 class="font-weight-medium mb-1">
                                                <v-icon small class="mr-1">mdi-account</v-icon>User Message
                                            </h6>
                                            <v-card outlined class="preview-card">
                                                <v-card-text class="pa-3">
                                                    <pre class="preview-content">{{ previewData.user_message || '(empty)' }}</pre>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </template>

                                <!-- Full Prompt View -->
                                <template v-else-if="previewViewMode === 'full'">
                                    <v-card outlined class="preview-card">
                                        <v-card-text class="pa-3">
                                            <div class="mb-2">
                                                <v-chip x-small color="blue" text-color="white" class="mr-1">SYSTEM</v-chip>
                                            </div>
                                            <pre class="preview-content mb-3">{{ previewData.system_message || '(empty)' }}</pre>
                                            <v-divider class="mb-3" />
                                            <div class="mb-2">
                                                <v-chip x-small color="green" text-color="white" class="mr-1">USER</v-chip>
                                            </div>
                                            <pre class="preview-content">{{ previewData.user_message || '(empty)' }}</pre>
                                        </v-card-text>
                                    </v-card>
                                </template>

                                <!-- Data Map View -->
                                <template v-else-if="previewViewMode === 'data'">
                                    <v-alert v-if="notConfiguredCount > 0" type="info" dense text class="mb-3">
                                        <strong>{{ notConfiguredCount }}</strong> field(s) have no default value configured — they will be empty in the prompt.
                                        Set default values in the Schema tab or provide sample values above.
                                    </v-alert>
                                    <v-simple-table dense class="schema-table">
                                        <thead>
                                            <tr>
                                                <th>Alias / Key</th>
                                                <th>Resolved Value</th>
                                                <th>Source</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(val, key) in previewData.substitution_map" :key="key">
                                                <td class="font-weight-medium text-no-wrap">
                                                    <code class="px-1">${{ '{' + key + '}' }}</code>
                                                </td>
                                                <td>
                                                    <span v-if="val !== '' && val != null">{{ val }}</span>
                                                    <span v-else class="grey--text font-italic">(empty)</span>
                                                </td>
                                                <td>
                                                    <v-chip x-small :color="valueSourceColor(previewData.value_sources && previewData.value_sources[key])" outlined>
                                                        {{ valueSourceLabel(previewData.value_sources && previewData.value_sources[key]) }}
                                                    </v-chip>
                                                </td>
                                                <td>
                                                    <v-icon small :color="val !== '' && val != null ? 'success' : 'warning'">
                                                        {{ val !== '' && val != null ? 'mdi-check-circle' : 'mdi-alert' }}
                                                    </v-icon>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                </template>
                            </template>

                            <!-- Empty state -->
                            <div v-else-if="!previewLoading" class="text-center py-6 grey--text">
                                <v-icon large class="mb-2">mdi-file-eye-outline</v-icon>
                                <div class="text-body-2">Click <strong>Generate Preview</strong> to see how the full prompt will be constructed</div>
                            </div>

                            <!-- AI Test Response -->
                            <template v-if="testPromptResponse">
                                <v-divider class="my-4" />
                                <div class="d-flex align-center mb-2">
                                    <v-icon small color="deep-purple" class="mr-2">mdi-robot</v-icon>
                                    <h6 class="font-weight-medium">AI Response</h6>
                                    <v-spacer />
                                    <v-chip v-if="testPromptResponse.usage" x-small color="deep-purple" outlined class="mr-2">
                                        Tokens: {{ testPromptResponse.usage.total_tokens || '—' }}
                                    </v-chip>
                                    <v-btn icon small @click="copyTestResponse" title="Copy AI response">
                                        <v-icon small>mdi-content-copy</v-icon>
                                    </v-btn>
                                </div>
                                <v-card outlined class="preview-card">
                                    <v-card-text class="pa-3">
                                        <pre class="preview-content" style="white-space: pre-wrap;">{{ testPromptResponse.ai_response }}</pre>
                                    </v-card-text>
                                </v-card>
                            </template>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </template>

            <!-- No schema yet -->
            <div v-else class="text-center py-4 grey--text">
                <v-icon class="mb-2">mdi-information-outline</v-icon>
                <div>No input schema configured for this feature.</div>
                <v-btn v-if="editable" text small color="primary" class="mt-2" @click="createDefaultSchema">
                    <v-icon left small>mdi-plus</v-icon>Create Default Schema
                </v-btn>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
export default {
    name: 'AiInputSchemaEditor',
    props: {
        featureName: {
            type: String,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        allowStaticOverrides: {
            type: Boolean,
            default: false
        },
        featureData: {
            type: Object,
            default: () => ({})
        },
        clientId: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            activeTab: 0,
            useInputSchema: false,
            localSchema: null,
            schemaSaving: false,
            staticOverrides: [],
            dataTypes: ['string', 'number', 'boolean', 'date'],
            runtimeDataTypes: ['string', 'number', 'enum', 'boolean'],
            cardinalityOptions: ['scalar', 'object', 'array'],
            contextSources: ['client', 'reporting_unit', 'country', 'gl', 'period'],
            promptTypes: ['instruction', 'output', 'business_context', 'guard_rails', 'input'],
            availableCollections: [],
            collectionsLoading: false,
            collectionFieldsCache: {},
            collectionRefCache: {},
            fieldLoadingIndex: -1,
            previewCycleType: 'proforma',
            previewTypeOfData: 'proforma',
            previewSampleValues: {},
            sampleValueMeta: [],
            previewViewMode: 'split',
            // Prompt editor state
            editablePromptTypes: [],
            promptEditorLoading: false,
            autocomplete: {
                visible: false,
                promptType: null,
                filter: '',
                top: 0,
                left: 0,
                selectedIndex: 0,
                triggerStart: -1,
            },
            // Test prompt state
            testPromptLoading: false,
            testPromptResponse: null,
        };
    },
    computed: {
        schemaLoading() {
            return this.$store.getters['admin/getInputSchemaLoading'];
        },
        previewLoading() {
            return this.$store.getters['admin/getInputSchemaPreviewLoading'];
        },
        previewData() {
            return this.$store.getters['admin/getInputSchemaPreview'];
        },
        notConfiguredCount() {
            const sources = this.previewData?.value_sources;
            if (!sources) return 0;
            return Object.values(sources).filter(s => s === 'not_configured').length;
        },
        effectiveStaticInputs() {
            if (!this.localSchema) return [];
            if (this.editable) return this.localSchema.static_inputs;
            // Platform admin: show master values with overrides applied
            const inputs = JSON.parse(JSON.stringify(this.localSchema.static_inputs || []));
            if (Array.isArray(this.staticOverrides)) {
                for (const ov of this.staticOverrides) {
                    const match = inputs.find(si => si.key === ov.key);
                    if (match) match.value = ov.value;
                }
            }
            return inputs;
        },
        estimatedTokens() {
            if (!this.previewData) return 0;
            const sys = this.previewData.system_message || '';
            const usr = this.previewData.user_message || '';
            return Math.ceil((sys.length + usr.length) / 4);
        },
        sampleValueEntries() {
            return this.sampleValueMeta;
        },
        filteredAutocompleteItems() {
            const all = this.getAllAliases();
            if (!this.autocomplete.filter) return all;
            return all.filter(item => item.alias.toLowerCase().includes(this.autocomplete.filter));
        }
    },
    watch: {
        activeTab(newVal) {
            // Tab 2 is Prompt Editor - load prompts when tab is selected
            if (newVal === 2 && !this.editablePromptTypes.length && !this.promptEditorLoading) {
                this.loadPrompts();
            }
        },
        featureName: {
            handler(newVal) {
                if (newVal) {
                    this.useInputSchema = !!this.featureData?.use_input_schema;
                    this.staticOverrides = this.featureData?.static_input_overrides || [];
                    this.$store.commit('admin/clearInputSchema');
                    if (this.useInputSchema) {
                        this.loadSchema();
                    }
                }
            },
            immediate: true
        },
        featureData: {
            handler(newVal) {
                if (newVal) {
                    this.useInputSchema = !!newVal.use_input_schema;
                    this.staticOverrides = newVal.static_input_overrides || [];
                }
            },
            deep: true
        }
    },
    methods: {
        async loadSchema() {
            try {
                const schema = await this.$store.dispatch('admin/fetchInputSchema', {
                    axiosInstance: this.axios,
                    feature: this.featureName
                });
                this.localSchema = schema ? JSON.parse(JSON.stringify(schema)) : null;
                if (this.localSchema) {
                    // Ensure target_blocks array exists
                    if (!Array.isArray(this.localSchema.target_blocks)) {
                        this.$set(this.localSchema, 'target_blocks', []);
                    }
                    // Ensure cardinality fields exist on loaded field_mappings
                    for (const fm of (this.localSchema.field_mappings || [])) {
                        if (!fm.cardinality) this.$set(fm, 'cardinality', 'scalar');
                        if (!fm.format) this.$set(fm, 'format', {});
                        if (!fm.nested_mappings) this.$set(fm, 'nested_mappings', []);
                        this.$set(fm, '_expanded', false);
                    }
                }
                if (this.editable) {
                    this.loadCollections();
                    // Pre-load fields for existing field mappings
                    if (this.localSchema?.field_mappings) {
                        for (const fm of this.localSchema.field_mappings) {
                            if (fm.source_collection && !this.collectionFieldsCache[fm.source_collection]) {
                                this.loadFieldsForCollection(fm.source_collection);
                            }
                        }
                    }
                }
                // Auto-generate sample values for preview
                if (this.localSchema) {
                    this.generateSampleValues();
                }
            } catch (error) {
                console.error('Error loading input schema:', error);
                this.localSchema = null;
            }
        },
        async loadCollections() {
            if (this.availableCollections.length) return;
            this.collectionsLoading = true;
            try {
                const collections = await this.$store.dispatch('admin/fetchCollections', {
                    axiosInstance: this.axios
                });
                this.availableCollections = collections;
            } catch (error) {
                console.error('Error loading collections:', error);
            } finally {
                this.collectionsLoading = false;
            }
        },
        async loadFieldsForCollection(collectionName) {
            if (this.collectionFieldsCache[collectionName]) return;
            try {
                const [fields, refs] = await Promise.all([
                    this.$store.dispatch('admin/fetchCollectionFields', {
                        axiosInstance: this.axios,
                        collection: collectionName
                    }),
                    this.$store.dispatch('admin/fetchCollectionRefFields', {
                        axiosInstance: this.axios,
                        collection: collectionName
                    })
                ]);
                this.$set(this.collectionFieldsCache, collectionName, fields);
                this.$set(this.collectionRefCache, collectionName, refs);
            } catch (error) {
                console.error('Error loading fields for', collectionName, error);
            }
        },
        async onCollectionChange(field, idx) {
            field.source_field = '';
            if (!field.source_collection) return;
            this.fieldLoadingIndex = idx;
            await this.loadFieldsForCollection(field.source_collection);
            this.fieldLoadingIndex = -1;
        },
        onSourceFieldChange(field) {
            // Auto-detect ObjectId ref and set lookup_collection
            const ref = this.getRefInfo(field);
            if (ref) {
                this.$set(field, 'lookup_collection', ref.ref_collection);
                // Pre-load lookup collection fields
                this.loadFieldsForCollection(ref.ref_collection);
            } else {
                this.$set(field, 'lookup_collection', '');
                this.$set(field, 'lookup_field', '');
            }
        },
        getRefInfo(field) {
            if (!field?.source_collection || !field?.source_field) return null;
            const refs = this.collectionRefCache[field.source_collection];
            if (!refs) return null;
            return refs[field.source_field] || null;
        },
        getNestedRefInfo(parentField, nestedMapping) {
            if (!parentField?.source_collection || !nestedMapping?.source_field) return null;
            const refs = this.collectionRefCache[parentField.source_collection];
            if (!refs) return null;
            return refs[nestedMapping.source_field] || null;
        },
        getNestedLookupFields(nestedMapping) {
            if (!nestedMapping?.lookup_collection) return [];
            return this.collectionFieldsCache[nestedMapping.lookup_collection] || [];
        },
        onNestedSourceFieldChange(parentField, nestedMapping) {
            const ref = this.getNestedRefInfo(parentField, nestedMapping);
            if (ref) {
                this.$set(nestedMapping, 'lookup_collection', ref.ref_collection);
                this.$set(nestedMapping, 'lookup_field', '');
                this.loadFieldsForCollection(ref.ref_collection);
            } else {
                this.$set(nestedMapping, 'lookup_collection', '');
                this.$set(nestedMapping, 'lookup_field', '');
            }
        },
        getLookupFields(field) {
            if (!field?.lookup_collection) return [];
            return this.collectionFieldsCache[field.lookup_collection] || [];
        },
        applyLookup(field) {
            const ref = this.getRefInfo(field);
            if (ref) {
                this.$set(field, 'lookup_collection', ref.ref_collection);
                this.loadFieldsForCollection(ref.ref_collection);
            }
        },
        clearLookup(field) {
            this.$set(field, 'lookup_collection', '');
            this.$set(field, 'lookup_field', '');
        },
        getFieldsForRow(idx) {
            const field = this.localSchema?.field_mappings?.[idx];
            if (!field?.source_collection) return [];
            return this.collectionFieldsCache[field.source_collection] || [];
        },
        getNestedFieldsForRow(parentIdx) {
            const parent = this.localSchema?.field_mappings?.[parentIdx];
            if (!parent?.source_collection) return [];
            const allFields = this.collectionFieldsCache[parent.source_collection] || [];
            // If no source_field (object type without root), show all fields from collection
            if (!parent.source_field) return allFields;
            const prefix = parent.source_field + '.';
            return allFields
                .filter(f => f.startsWith(prefix))
                .map(f => f.slice(prefix.length))
                .filter(f => !f.includes('.')); // only direct children, not deeper paths
        },
        getObjectRootFields(idx) {
            // Return fields that have children (i.e. are parent objects) for the object root picker
            const field = this.localSchema?.field_mappings?.[idx];
            if (!field?.source_collection) return [];
            const allFields = this.collectionFieldsCache[field.source_collection] || [];
            const parents = new Set();
            for (const f of allFields) {
                const dotIdx = f.indexOf('.');
                if (dotIdx > 0) parents.add(f.substring(0, dotIdx));
            }
            return [...parents].sort();
        },
        getObjectTemplatePlaceholder(field) {
            if (!field.nested_mappings?.length) return 'e.g. {${key1}: ${key2}}';
            return field.nested_mappings.map(nm => `${nm.alias || 'key'}: \${${nm.alias || 'key'}}`).join(', ');
        },
        getArrayItemTemplatePlaceholder(field) {
            if (!field.nested_mappings?.length) return 'e.g. | ${Name}: ${Amount}';
            return '| ' + field.nested_mappings.map(nm => `\${${nm.alias || 'field'}}`).join(' | ');
        },
        getObjectPreview(field) {
            const keys = (field.nested_mappings || []).map(nm => `${nm.alias}: <${nm.source_field || '?'}>`);
            if (field.format?.template) {
                // Show template with sample values
                let tmpl = field.format.template;
                for (const nm of (field.nested_mappings || [])) {
                    tmpl = tmpl.replace(new RegExp(`\\$\\{${nm.alias}\\}`, 'g'), `<${nm.source_field || '?'}>`);
                }
                return tmpl;
            }
            return '{' + keys.join(', ') + '}';
        },
        getArrayPreview(field) {
            const itemTmpl = field.format?.item_template;
            if (itemTmpl) {
                let preview = itemTmpl;
                for (const nm of (field.nested_mappings || [])) {
                    preview = preview.replace(new RegExp(`\\$\\{${nm.alias}\\}`, 'g'), `<${nm.source_field || '?'}>`);
                }
                const sep = field.format?.separator || '\\n';
                return `${preview}${sep}${preview}...`;
            }
            const fields = (field.nested_mappings || []).map(nm => nm.alias || '?').join(', ');
            return `[{${fields}}, {${fields}}, ...]`;
        },
        onToggleChange(val) {
            this.$emit('toggle-schema', val);
            if (val && !this.localSchema) {
                this.loadSchema();
            }
        },

        // Field mappings
        addFieldMapping() {
            this.localSchema.field_mappings.push({
                source_collection: 'varianceanalysis_data',
                source_field: '',
                alias: '',
                cardinality: 'scalar',
                data_type: 'string',
                default_value: '',
                required: false,
                applicable_when: null,
                format: {},
                nested_mappings: []
            });
        },
        removeFieldMapping(idx) {
            this.localSchema.field_mappings.splice(idx, 1);
            this.saveSchema();
        },

        // Nested mapping helpers
        addNestedMapping(parentIdx) {
            const parent = this.localSchema.field_mappings[parentIdx];
            if (!parent.nested_mappings) this.$set(parent, 'nested_mappings', []);
            parent.nested_mappings.push({
                source_field: '',
                alias: '',
                data_type: 'string',
                default_value: ''
            });
        },
        removeNestedMapping(parentIdx, nestedIdx) {
            this.localSchema.field_mappings[parentIdx].nested_mappings.splice(nestedIdx, 1);
        },
        onCardinalityChange(idx) {
            const field = this.localSchema.field_mappings[idx];
            if (field.cardinality === 'scalar') {
                this.$set(field, 'format', {});
                this.$set(field, 'nested_mappings', []);
                this.$set(field, '_expanded', false);
            } else {
                if (!field.format) this.$set(field, 'format', {});
                if (!field.nested_mappings) this.$set(field, 'nested_mappings', []);
                this.$set(field, '_expanded', true);
            }
        },

        // Context mappings
        addContextMapping() {
            this.localSchema.context_mappings.push({
                source: 'client',
                source_field: '',
                alias: ''
            });
        },
        removeContextMapping(idx) {
            this.localSchema.context_mappings.splice(idx, 1);
        },

        // Static inputs
        addStaticInput() {
            this.localSchema.static_inputs.push({ key: '', value: '' });
        },
        removeStaticInput(idx) {
            this.localSchema.static_inputs.splice(idx, 1);
        },
        onStaticValueChange(si) {
            if (!this.editable && this.allowStaticOverrides) {
                // Track override
                const existing = this.staticOverrides.findIndex(o => o.key === si.key);
                if (existing >= 0) {
                    this.staticOverrides[existing].value = si.value;
                } else {
                    this.staticOverrides.push({ key: si.key, value: si.value });
                }
                this.$emit('static-overrides-change', this.staticOverrides);
            }
        },

        // Runtime inputs
        addRuntimeInput() {
            this.localSchema.runtime_inputs.push({
                key: '', data_type: 'string', required: false, default: '', max_length: null
            });
        },
        removeRuntimeInput(idx) {
            this.localSchema.runtime_inputs.splice(idx, 1);
        },

        // Target block helpers
        addTargetBlock() {
            this.localSchema.target_blocks.push({
                condition_field: 'type_of_data',
                condition_value: '',
                fields: []
            });
        },
        removeTargetBlock(idx) {
            this.localSchema.target_blocks.splice(idx, 1);
        },
        addTargetBlockField(blockIdx) {
            this.localSchema.target_blocks[blockIdx].fields.push({
                label: '', source_field: ''
            });
        },
        removeTargetBlockField(blockIdx, fieldIdx) {
            this.localSchema.target_blocks[blockIdx].fields.splice(fieldIdx, 1);
        },

        // Applicable when helpers
        formatApplicableWhen(obj) {
            if (!obj || typeof obj !== 'object') return '';
            return Object.entries(obj).map(([k, v]) => `${k}=${v}`).join(', ');
        },
        parseApplicableWhen(field, val) {
            if (!val || !val.trim()) {
                field.applicable_when = null;
                return;
            }
            const result = {};
            val.split(',').forEach(pair => {
                const [k, v] = pair.split('=').map(s => s.trim());
                if (k && v) result[k] = v;
            });
            field.applicable_when = Object.keys(result).length ? result : null;
        },

        // Create default schema
        async createDefaultSchema() {
            const defaultSchema = {
                field_mappings: [],
                context_mappings: [],
                static_inputs: [],
                computed_inputs: [],
                runtime_inputs: [],
                composition: {
                    system_message: ['instruction', 'business_context'],
                    user_message: ['input', 'output', 'guard_rails']
                },
                global_replacements: [],
                legacy_placeholder_map: [],
                target_blocks: []
            };
            try {
                await this.$store.dispatch('admin/saveInputSchema', {
                    axiosInstance: this.axios,
                    feature: this.featureName,
                    schema: defaultSchema
                });
                this.$toast?.success('Input schema created');
                await this.loadSchema();
            } catch (error) {
                this.$toast?.error(error.message || 'Failed to create schema');
            }
        },

        // Save schema (called from parent)
        async saveSchema() {
            if (!this.localSchema) return;
            // Validate unique aliases
            const aliases = [];
            for (const arr of [this.localSchema.field_mappings, this.localSchema.context_mappings]) {
                for (const item of (arr || [])) {
                    if (item.alias && aliases.includes(item.alias)) {
                        this.$toast?.error(`Duplicate alias: ${item.alias}`);
                        return false;
                    }
                    if (item.alias) aliases.push(item.alias);
                    // Validate nested_mappings aliases within parent scope
                    if (Array.isArray(item.nested_mappings)) {
                        const nestedAliases = [];
                        for (const nested of item.nested_mappings) {
                            if (nested.alias && nestedAliases.includes(nested.alias)) {
                                this.$toast?.error(`Duplicate nested alias in ${item.alias}: ${nested.alias}`);
                                return false;
                            }
                            if (nested.alias) nestedAliases.push(nested.alias);
                        }
                    }
                }
            }
            for (const item of (this.localSchema.static_inputs || [])) {
                if (item.key && aliases.includes(item.key)) {
                    this.$toast?.error(`Duplicate alias: ${item.key}`);
                    return false;
                }
                if (item.key) aliases.push(item.key);
            }

            const storeSchema = this.$store.getters['admin/getInputSchema'];
            const action = storeSchema?._id ? 'admin/updateInputSchema' : 'admin/saveInputSchema';
            this.schemaSaving = true;
            try {
                await this.$store.dispatch(action, {
                    axiosInstance: this.axios,
                    feature: this.featureName,
                    schema: {
                        field_mappings: (this.localSchema.field_mappings || []).map(fm => {
                            // eslint-disable-next-line no-unused-vars
                            const { _expanded, ...rest } = fm;
                            return rest;
                        }),
                        context_mappings: this.localSchema.context_mappings,
                        static_inputs: this.localSchema.static_inputs,
                        computed_inputs: this.localSchema.computed_inputs || [],
                        runtime_inputs: this.localSchema.runtime_inputs,
                        composition: this.localSchema.composition,
                        global_replacements: this.localSchema.global_replacements || [],
                        legacy_placeholder_map: this.localSchema.legacy_placeholder_map || [],
                        target_blocks: this.localSchema.target_blocks || []
                    }
                });
                this.$toast?.success('Schema saved successfully');
                return true;
            } catch (error) {
                this.$toast?.error(error.message || 'Failed to save schema');
                return false;
            } finally {
                this.schemaSaving = false;
            }
        },

        // Preview
        async runPreview() {
            if (!Object.keys(this.previewSampleValues).length) {
                this.generateSampleValues();
            }
            try {
                await this.$store.dispatch('admin/previewInputSchema', {
                    axiosInstance: this.axios,
                    payload: {
                        feature: this.featureName,
                        client_id: this.clientId,
                        cycle_type: this.previewCycleType,
                        type_of_data: this.previewTypeOfData,
                        sample_values: this.previewSampleValues,
                        static_input_overrides: this.staticOverrides || []
                    }
                });
            } catch (error) {
                this.$toast?.error(error.message || 'Preview failed');
            }
        },
        async runTestPrompt() {
            if (!Object.keys(this.previewSampleValues).length) {
                this.generateSampleValues();
            }
            this.testPromptLoading = true;
            this.testPromptResponse = null;
            try {
                const result = await this.$store.dispatch('admin/testPrompt', {
                    axiosInstance: this.axios,
                    payload: {
                        feature: this.featureName,
                        client_id: this.clientId,
                        cycle_type: this.previewCycleType,
                        type_of_data: this.previewTypeOfData,
                        sample_values: this.previewSampleValues,
                        static_input_overrides: this.staticOverrides || []
                    }
                });
                this.testPromptResponse = result;
            } catch (error) {
                this.$toast?.error(error.message || 'Test prompt failed');
            } finally {
                this.testPromptLoading = false;
            }
        },
        copyTestResponse() {
            if (this.testPromptResponse?.ai_response) {
                navigator.clipboard.writeText(this.testPromptResponse.ai_response);
                this.$toast?.success('AI response copied to clipboard');
            }
        },
        generateSampleValues() {
            if (!this.localSchema) return;
            const values = {};
            const meta = [];
            // Field mappings (cardinality-aware) — only use schema defaults, no synthetic values
            for (const fm of (this.localSchema.field_mappings || [])) {
                if (!fm.alias) continue;
                const cardinality = fm.cardinality || 'scalar';
                if (cardinality === 'scalar') {
                    let val = this.previewSampleValues[fm.alias];
                    if (!val && fm.default_value) val = fm.default_value;
                    if (!val) val = '';
                    values[fm.alias] = val;
                    if (fm.source_field) {
                        values[`responseDoc.${fm.source_field}`] = val;
                    }
                    meta.push({ key: fm.alias, source: 'field', dataType: fm.data_type || 'string', defaultValue: fm.default_value || '', hasDefault: !!fm.default_value });
                } else if (cardinality === 'object') {
                    if (this.previewSampleValues[fm.alias]) {
                        values[fm.alias] = this.previewSampleValues[fm.alias];
                    } else {
                        const childCtx = {};
                        for (const nested of (fm.nested_mappings || [])) {
                            childCtx[nested.alias] = nested.default_value || '';
                        }
                        const tmpl = fm.format?.template;
                        if (!tmpl) {
                            values[fm.alias] = JSON.stringify(childCtx);
                        } else {
                            values[fm.alias] = tmpl.replace(/\$\{([^}]+)\}/g, (m, k) => childCtx[k.trim()] ?? m);
                        }
                    }
                    meta.push({ key: fm.alias, source: 'field', dataType: 'object', defaultValue: '', hasDefault: false });
                } else if (cardinality === 'array') {
                    if (this.previewSampleValues[fm.alias]) {
                        values[fm.alias] = this.previewSampleValues[fm.alias];
                    } else {
                        const itemTmpl = fm.format?.item_template || '';
                        const sep = fm.format?.separator || '\n';
                        const nestedMappings = fm.nested_mappings || [];
                        if (!nestedMappings.length) {
                            values[fm.alias] = fm.format?.empty_text || '';
                        } else {
                            const items = [1, 2].map(i => {
                                const childCtx = {};
                                for (const nested of nestedMappings) {
                                    const base = nested.default_value || '';
                                    childCtx[nested.alias] = typeof base === 'string' && base ? `${base}_${i}` : base;
                                }
                                return itemTmpl.replace(/\$\{([^}]+)\}/g, (m, k) => childCtx[k.trim()] ?? m);
                            });
                            values[fm.alias] = items.join(sep);
                        }
                    }
                    meta.push({ key: fm.alias, source: 'field', dataType: 'array', defaultValue: '', hasDefault: false });
                }
            }
            // Context mappings
            for (const cm of (this.localSchema.context_mappings || [])) {
                if (!cm.alias) continue;
                let val = this.previewSampleValues[cm.alias];
                if (!val && cm.default_value) val = cm.default_value;
                if (!val) val = '';
                values[cm.alias] = val;
                meta.push({ key: cm.alias, source: 'context', dataType: 'string', hasDefault: !!cm.default_value });
            }
            // Static inputs
            for (const si of (this.localSchema.static_inputs || [])) {
                if (!si.key) continue;
                values[si.key] = this.previewSampleValues[si.key] || si.value || '';
                meta.push({ key: si.key, source: 'static', dataType: 'string', hasDefault: !!si.value });
            }
            // Computed inputs
            for (const ci of (this.localSchema.computed_inputs || [])) {
                if (!ci.key) continue;
                values[ci.key] = this.previewSampleValues[ci.key] || '';
                meta.push({ key: ci.key, source: 'computed', dataType: 'number', hasDefault: false });
            }
            // Runtime inputs
            for (const ri of (this.localSchema.runtime_inputs || [])) {
                if (!ri.key) continue;
                values[ri.key] = this.previewSampleValues[ri.key] || ri.default || '';
                meta.push({ key: ri.key, source: 'runtime', dataType: ri.data_type || 'string', hasDefault: !!ri.default });
            }
            // Target block fields
            for (const block of (this.localSchema.target_blocks || [])) {
                for (const f of (block.fields || [])) {
                    if (!f.source_field) continue;
                    const legacyKey = `responseDoc.${f.source_field}`;
                    if (!values[legacyKey]) {
                        values[legacyKey] = this.previewSampleValues[legacyKey] || '';
                    }
                    if (f.label && !values[f.label]) {
                        values[f.label] = values[legacyKey];
                    }
                    meta.push({ key: legacyKey, source: 'target_block', dataType: 'string', hasDefault: false });
                }
            }
            this.previewSampleValues = values;
            this.sampleValueMeta = meta;
        },
        sourceColor(source) {
            const map = { field: 'blue', context: 'purple', static: 'teal', computed: 'orange', runtime: 'green', target_block: 'deep-orange' };
            return map[source] || 'grey';
        },
        valueSourceColor(source) {
            const map = { user_input: 'blue', schema_default: 'teal', static_default: 'teal', not_configured: 'red' };
            return map[source] || 'grey';
        },
        valueSourceLabel(source) {
            const map = { user_input: 'User Input', schema_default: 'Default', static_default: 'Static', not_configured: 'Not Set' };
            return map[source] || source || '—';
        },

        // ===== Prompt Editor =====
        async loadPrompts() {
            if (!this.featureName) return;
            this.promptEditorLoading = true;
            try {
                const types = ['instruction', 'output', 'business_context', 'input', 'guard_rails'];
                const prompts = [];
                for (const type of types) {
                    try {
                        const list = await this.$store.dispatch('admin/fetchPromptList', {
                            axiosInstance: this.axios,
                            feature: this.featureName,
                            type,
                        });
                        if (list && list.length) {
                            const p = list.find(l => l.status !== false) || list[0];
                            prompts.push({
                                _id: p._id,
                                type: p.type,
                                feature: p.feature || this.featureName,
                                name: p.name || '',
                                content: p.content || '',
                                status: p.status !== false,
                                _saving: false,
                                _original: p.content || '',
                            });
                        } else {
                            // No prompt exists for this type — add empty placeholder
                            prompts.push({
                                _id: null,
                                type,
                                feature: this.featureName,
                                name: '',
                                content: '',
                                status: true,
                                _saving: false,
                                _original: '',
                            });
                        }
                    } catch (e) {
                        // API error — still show empty placeholder so user can create it
                        prompts.push({
                            _id: null,
                            type,
                            feature: this.featureName,
                            name: '',
                            content: '',
                            status: true,
                            _saving: false,
                            _original: '',
                        });
                    }
                }
                this.editablePromptTypes = prompts;
            } catch (err) {
                console.error('Error loading prompts for editor:', err);
            } finally {
                this.promptEditorLoading = false;
            }
        },

        async savePrompt(pt) {
            pt._saving = true;
            try {
                if (pt._id) {
                    // Update existing prompt
                    await this.$store.dispatch('admin/updatePromptById', {
                        axiosInstance: this.axios,
                        promptData: {
                            _id: pt._id,
                            content: pt.content,
                            type: pt.type,
                            name: pt.name,
                            status: pt.status,
                        },
                    });
                } else {
                    // Create new prompt
                    const resp = await this.$store.dispatch('admin/createFeaturePrompt', {
                        axiosInstance: this.axios,
                        featureData: {
                            feature: pt.feature,
                            type: pt.type,
                            content: pt.content,
                            name: pt.name || pt.type,
                            status: pt.status,
                        },
                    });
                    // Assign the new _id so future saves update instead of create
                    if (resp?.data?._id) {
                        pt._id = resp.data._id;
                    } else if (resp?.data?.length && resp.data[0]?._id) {
                        pt._id = resp.data[0]._id;
                    }
                }
                pt._original = pt.content;
                this.$toast?.success(`Prompt "${pt.type}" saved`);
            } catch (err) {
                this.$toast?.error(err.message || 'Failed to save prompt');
            } finally {
                pt._saving = false;
            }
        },

        // Build list of all available aliases for autocomplete
        getAllAliases() {
            if (!this.localSchema) return [];
            const items = [];
            for (const fm of (this.localSchema.field_mappings || [])) {
                if (fm.alias) items.push({ alias: fm.alias, source: 'field', dataType: fm.data_type || 'string' });
                // Include nested aliases for object/array cardinality
                if (fm.cardinality && fm.cardinality !== 'scalar') {
                    for (const nm of (fm.nested_mappings || [])) {
                        if (nm.alias) items.push({ alias: fm.alias + '.' + nm.alias, source: 'field.nested', dataType: nm.data_type || 'string' });
                    }
                }
            }
            for (const cm of (this.localSchema.context_mappings || [])) {
                if (cm.alias) items.push({ alias: cm.alias, source: 'context', dataType: 'string' });
            }
            for (const si of (this.localSchema.static_inputs || [])) {
                if (si.key) items.push({ alias: si.key, source: 'static', dataType: 'string' });
            }
            for (const ci of (this.localSchema.computed_inputs || [])) {
                if (ci.key) items.push({ alias: ci.key, source: 'computed', dataType: 'number' });
            }
            for (const ri of (this.localSchema.runtime_inputs || [])) {
                if (ri.key) items.push({ alias: ri.key, source: 'runtime', dataType: ri.data_type || 'string' });
            }
            return items;
        },

        onPromptInput(value, pt) {
            // Detect $$ trigger
            const refKey = 'promptTextarea_' + pt.type;
            const textareaRef = this.$refs[refKey];
            if (!textareaRef || !textareaRef[0]) return;
            const el = textareaRef[0].$el.querySelector('textarea');
            if (!el) return;
            const cursorPos = el.selectionStart;
            const textBefore = value.substring(0, cursorPos);

            // Find last $$ occurrence
            const triggerIdx = textBefore.lastIndexOf('$$');
            if (triggerIdx === -1 || triggerIdx < this.autocomplete.triggerStart - 1) {
                this.closeAutocomplete();
                return;
            }

            // Extract filter text between $$ and cursor
            const filterText = textBefore.substring(triggerIdx + 2);
            // If there's a space or newline in the filter, close
            if (/[\s\n]/.test(filterText)) {
                this.closeAutocomplete();
                return;
            }

            // Position the menu near the textarea
            const rect = el.getBoundingClientRect();
            // Approximate cursor position (rough)
            const lineHeight = 20;
            const lines = textBefore.split('\n');
            const currentLine = lines.length;
            this.autocomplete = {
                visible: true,
                promptType: pt.type,
                filter: filterText.toLowerCase(),
                top: Math.min(currentLine * lineHeight + 8, rect.height - 40),
                left: 12,
                selectedIndex: 0,
                triggerStart: triggerIdx,
            };
        },

        onPromptKeydown(event, pt) {
            if (!this.autocomplete.visible || this.autocomplete.promptType !== pt.type) return;

            const items = this.filteredAutocompleteItems;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.autocomplete.selectedIndex = Math.min(this.autocomplete.selectedIndex + 1, items.length - 1);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.autocomplete.selectedIndex = Math.max(this.autocomplete.selectedIndex - 1, 0);
            } else if (event.key === 'Enter' || event.key === 'Tab') {
                if (items.length) {
                    event.preventDefault();
                    this.insertAutocomplete(items[this.autocomplete.selectedIndex], pt);
                }
            } else if (event.key === 'Escape') {
                event.preventDefault();
                this.closeAutocomplete();
            }
        },

        insertAutocomplete(item, pt) {
            const refKey = 'promptTextarea_' + pt.type;
            const textareaRef = this.$refs[refKey];
            if (!textareaRef || !textareaRef[0]) return;
            const el = textareaRef[0].$el.querySelector('textarea');
            if (!el) return;

            const cursorPos = el.selectionStart;
            const triggerIdx = this.autocomplete.triggerStart;
            const before = pt.content.substring(0, triggerIdx);
            const after = pt.content.substring(cursorPos);
            const insertion = '${' + item.alias + '}';
            pt.content = before + insertion + after;

            this.closeAutocomplete();

            // Restore cursor position after Vue re-renders
            this.$nextTick(() => {
                const newPos = triggerIdx + insertion.length;
                el.setSelectionRange(newPos, newPos);
                el.focus();
            });
        },

        closeAutocomplete() {
            this.autocomplete = {
                visible: false,
                promptType: null,
                filter: '',
                top: 0,
                left: 0,
                selectedIndex: 0,
                triggerStart: -1,
            };
        },
        copyPreviewToClipboard() {
            if (!this.previewData) return;
            const text = `--- SYSTEM ---\n${this.previewData.system_message || ''}\n\n--- USER ---\n${this.previewData.user_message || ''}`;
            navigator.clipboard.writeText(text).then(() => {
                this.$toast?.success('Prompt copied to clipboard');
            }).catch(() => {
                this.$toast?.error('Failed to copy');
            });
        },

        // Expose schema toggle state for parent save flow
        getSchemaPayload() {
            return {
                use_input_schema: this.useInputSchema,
                static_input_overrides: this.staticOverrides
            };
        }
    }
};
</script>

<style scoped>
.schema-table {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
}
.nested-table {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fafafa;
}
.field-mapping-card {
    border-left: 3px solid #1976d2;
}
.schema-input {
    font-size: 13px;
}
.schema-input ::v-deep .v-input__slot {
    min-height: 32px !important;
}
.preview-content {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    max-height: 400px;
    overflow-y: auto;
}
.preview-card {
    max-height: 500px;
    overflow-y: auto;
}
.prompt-editor-wrapper {
    position: relative;
}
.prompt-autocomplete-menu {
    position: absolute;
    z-index: 10;
    min-width: 280px;
    max-width: 400px;
    border: 1px solid #e0e0e0;
}
.prompt-editor-textarea ::v-deep textarea {
    font-family: 'Roboto Mono', monospace;
    font-size: 13px;
    line-height: 1.6;
}
</style>
